import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";

/**
 * Sentry initialisation for the SPA. Called once from `main.tsx` before the app renders.
 *
 * The DSN is the TC Travel Desk **web** project. A DSN is not a secret (the browser bundle has to
 * carry it), so it ships as the default; override at build time with `VITE_SENTRY_DSN`.
 *
 * Reporting is **off in dev** — it only runs in a production build (`import.meta.env.PROD`), so
 * `pnpm dev` and the local Vite server never send events.
 */
const DSN =
  import.meta.env.VITE_SENTRY_DSN ??
  "https://465d126b7be08912218b2c93e439f7f4@o268873.ingest.us.sentry.io/4511807694700544";

/**
 * Resolve the Sentry `environment` — the bucket events are grouped under — from `APP_ENV`, so the
 * web mirrors the API (which reads `APP_ENV` directly). The SPA is built ONCE and deployed to every
 * environment, so `APP_ENV` is not known at build time; instead the deploy writes it into
 * `deploy-info.json` (docker/after-deploy.sh), served at the web root. We read it at runtime and
 * fall back to the build-time value (`VITE_SENTRY_ENV`, else Vite's mode) if it is unavailable.
 */
async function resolveEnvironment(): Promise<string> {
  const fallback = import.meta.env.VITE_SENTRY_ENV ?? import.meta.env.MODE;
  try {
    const res = await fetch("/deploy-info.json", { cache: "no-store" });
    if (res.ok) {
      const info = (await res.json()) as { environment?: string };
      if (info.environment) return info.environment;
    }
  } catch {
    // Network/parse failure — keep the build-time fallback.
  }
  return fallback;
}

export async function initSentry(): Promise<void> {
  if (!import.meta.env.PROD || !DSN) return;

  // Resolve the environment before init so every event type (errors, traces, replays) is tagged
  // with the right bucket. This adds one same-origin static GET at startup; render is not blocked
  // on it (main.tsx does not await initSentry).
  const environment = await resolveEnvironment();

  Sentry.init({
    dsn: DSN,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      // Route-aware performance + navigation tracing for react-router v7 (declarative <Routes>).
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Session Replay — records a small sample of sessions, and any session where an error occurs.
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}
